import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { company } from '../data/company';
import { pageSeo, SITE_URL as DEFAULT_SITE_URL, CORE_KEYWORDS } from '../data/seo';

const SITE_URL = DEFAULT_SITE_URL;
const SITE_NAME = company.name;
// Social share card (1200×630, the size Facebook/WhatsApp/LinkedIn/X expect): logo + owner and team
// in front of the George warehouse. Regenerate public/og-image.jpg if the branding changes.
const DEFAULT_IMAGE = {
  url: `${SITE_URL}/og-image.jpg`,
  width: 1200,
  height: 630,
  type: 'image/jpeg',
  alt: `${company.name} — owner and team in front of the warehouse in Joubert Industria, George`,
};

export default function SEO({
  title,
  description,
  keywords,
  path,
  image,
  type = 'website',
  noindex = false,
  structuredData,
  breadcrumbs,
} = {}) {
  const { pathname } = useLocation();
  const resolvedPath = path || pathname;
  const pageData = pageSeo[resolvedPath] || {};

  const resolvedTitle = title || pageData.title;
  const resolvedDescription = description || pageData.description || company.description_short;
  const resolvedKeywords = keywords || pageData.keywords || CORE_KEYWORDS.join(', ');
  const resolvedBreadcrumbs = breadcrumbs || pageData.breadcrumbs;

  const fullTitle = resolvedTitle
    ? `${resolvedTitle} | ${SITE_NAME}`
    : `Ceiling and Drywall Supplies in George | ${SITE_NAME}`;
  const canonicalUrl = resolvedPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${resolvedPath}`;
  const metaImage = image || DEFAULT_IMAGE;

  const jsonLd = [].concat(structuredData || pageData.structuredData || []).filter(Boolean);
  if (resolvedBreadcrumbs && resolvedBreadcrumbs.length) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{ name: 'Home', path: '/' }, ...resolvedBreadcrumbs].map((crumb, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
        item: crumb.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${crumb.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={resolvedKeywords} />
      <meta
        name="robots"
        content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />
      {!noindex && <link rel="canonical" href={canonicalUrl} />}

      {/* Local business signals */}
      <meta name="geo.region" content="ZA-WC" />
      <meta name="geo.placename" content="George, Western Cape" />
      <meta name="geo.position" content={`${company.geo.latitude};${company.geo.longitude}`} />
      <meta name="ICBM" content={`${company.geo.latitude}, ${company.geo.longitude}`} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={metaImage.url} />
      <meta property="og:image:secure_url" content={metaImage.url} />
      {metaImage.type && <meta property="og:image:type" content={metaImage.type} />}
      {metaImage.width && <meta property="og:image:width" content={String(metaImage.width)} />}
      {metaImage.height && <meta property="og:image:height" content={String(metaImage.height)} />}
      {metaImage.alt && <meta property="og:image:alt" content={metaImage.alt} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={metaImage.url} />
      {metaImage.alt && <meta name="twitter:image:alt" content={metaImage.alt} />}

      {jsonLd.map((data, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
